import {AuthError, BrowserAuthError} from "@azure/msal-browser";

export type OneDriveItem = {
    id: string;
    name: string;
    size: number;
    webUrl: string;
};

export type ListChildrenResponse = {
    value: OneDriveItem[];
};

/**
 * Determine if a given value fits the shape of the OneDriveItem type.
 * OneDriveItem definition: https://learn.microsoft.com/en-us/graph/api/resources/driveitem?view=graph-rest-1.0
 *
 * @param val The value being checked.
 * @returns `true` if the value is a OneDrive item; otherwise, `false`.
 */
export function isOneDriveItem(val: any): val is OneDriveItem {
    return (
        typeof val === "object" &&
        val !== null &&
        typeof val.id === "string" &&
        typeof val.name === "string" &&
        typeof val.webUrl === "string" &&
        typeof val.size === "number"
    );
}

/**
 * Determine if a given value fits the shape of the ListChildren response type.
 * List Children Response definition: https://learn.microsoft.com/en-us/graph/api/driveitem-list-children?view=graph-rest-1.0&tabs=http#response
 *
 * @param val The value being checked.
 * @returns `true` if the value is a List Children response; otherwise, `false`.
 */
export function isListChildrenResponse(val: any): val is ListChildrenResponse {
    return (
        typeof val === "object" &&
        val !== null &&
        "value" in val &&
        Array.isArray(val.value) &&
        val.value.every(isOneDriveItem)
    );
}

/**
 * An Error returned from the Microsoft Graph API.
 * No Microsoft documentation available at time of writing.
 */
export type GraphAPIError = {
    /* The error code describing the specific error. */
    code: string;
    /* The status code; this is a standard HTTP status code such as 200 or 404. */
    statusCode: number;
    /* The error message. */
    message: string;
};

/**
 * Determine if a given value fits the shape of a GraphAPIError.
 *
 * @param val The value being checked.
 * @returns `true` if the value is a GraphAPIError; otherwise, `false`.
 */
export function isGraphAPIError(val: any): val is GraphAPIError {
    return (
        typeof val === "object" &&
        val !== null &&
        typeof val.code === "string" &&
        typeof val.statusCode === "number" &&
        typeof val.message === "string"
    );
}

/**
 * Determine if a given value fits the shape of a GraphAPIError related to the
 * User not having a SharePointOnline license.
 *
 * @param val The value being checked.
 * @returns `true` if the value is a GraphAPIError about missing a SPO licence;
 * otherwise, `false`.
 */
export function isMissingLicenseError(val: any): boolean {
    return isGraphAPIError(val) && val.message.includes("SPO license");
}

/**
 * Determine if a given value fits is an error about an interaction being in
 * progress. This error occurs when a login attempt is made while
 * another login attempt is already in progress.
 *
 * @param val The value being checked.
 * @returns `true` if the value is a BrowserAuthError and has the correct error
 * code; otherwise, `false`.
 */
export function isInteractionInProgressError(val: any): boolean {
    return (
        val instanceof BrowserAuthError &&
        val.errorCode === "interaction_in_progress"
    );
}

/**
 * Determine if a given value fits is an error about the user cancelling the
 * login process.  This error occurs when the user closes the login popup
 *
 * @param val The value being checked.
 * @returns `true` if the value is a BrowserAuthError and has the correct error
 * code; otherwise, `false`.
 */
export function isUserCancelledError(val: any): boolean {
    return (
        val instanceof BrowserAuthError && val.errorCode === "user_cancelled"
    );
}

/**
 * Determine if a given value fits is an error about the user cancelling the
 * login process.  This error occurs when the user successfully logs in with
 * Microsoft credentials, but does not consent to the app's permissions.
 * Occurs when the user clicks "No" on the consent screen or if the user does not have the required permissions to grant consent (some organizations limit consent permissions to admins)
 *
 * @param val The value being checked.
 * @returns `true` if the value is an AuthError and has the correct error
 * code; otherwise, `false`.
 */
export function isAccessDeniedError(val: any): boolean {
    return (
        val instanceof AuthError && // use AuthError because access denied error is not a BrowserAuthError
        val.errorCode === "access_denied"
    );
}
